import { inject, Injectable, signal } from '@angular/core';
import { BrowserPlatformService } from '../../shared/services/browser-platform.service';

const LOCAL_LAUNCH_PHASE_MODE_STORAGE_KEY = 'cmsLaunchPhaseMode';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export const RELEASED_MENU_ITEMS = [
  'Users',
  'Customers',
  'Applications',
  'Recommendations',
  'Approvals',
  'Withdrawn Certificates',
  'PT Cards',
  'Executive Dashboard',
  'Audits',
  'Maintenance',
  'Suppliers',
  'Suppliers Invoicing',
  'Customer Testing Invoices',
  'LTQLs',
  'Non-Compliances',
  'Raised Invoices',
  'Complaints',
  'Travel Advice',
  'Reporting',
] as const;

export const UNRELEASED_MENU_ITEMS = [] as const;

const RELEASED_MENU_ITEM_SET = new Set<string>(RELEASED_MENU_ITEMS);
const UNRELEASED_MENU_ITEM_SET = new Set<string>(UNRELEASED_MENU_ITEMS);
const UNRELEASED_ROUTE_PREFIXES = [] as const;
const ADMIN_TOOL_ROUTE_PREFIXES = ['/admin-tools'] as const;

export type LocalLaunchPhaseMode = 'phase-one' | 'both';

@Injectable({ providedIn: 'root' })
export class LaunchPhaseService {
  private readonly browser = inject(BrowserPlatformService);

  readonly isLocalTestingAvailable = this.isLocalHost();
  readonly localPhaseMode = signal<LocalLaunchPhaseMode>(
    this.readLocalPhaseMode(),
  );

  setLocalPhaseMode(value: LocalLaunchPhaseMode): void {
    this.localPhaseMode.set(value);
    this.writeLocalPhaseMode(value);
  }

  toggleLocalPhaseMode(): void {
    this.setLocalPhaseMode(this.localPhaseMode() === 'both' ? 'phase-one' : 'both');
  }

  canEnableMenuItem(name: string, isDcs: boolean): boolean {
    if (name === 'Admin Tools') {
      return this.canUseAdminTools(isDcs);
    }

    return (
      RELEASED_MENU_ITEM_SET.has(name) ||
      (UNRELEASED_MENU_ITEM_SET.has(name) && this.canUsePhaseTwo(isDcs))
    );
  }

  canOpenRoute(route: string, isDcs: boolean): boolean {
    if (this.isAdminToolsRoute(route)) {
      return this.canUseAdminTools(isDcs);
    }

    return !this.isPhaseTwoRoute(route) || this.canUsePhaseTwo(isDcs);
  }

  canUseAdminTools(isDcs: boolean): boolean {
    return isDcs;
  }

  canUsePhaseTwo(isDcs: boolean): boolean {
    if (this.isLocalTestingAvailable || isDcs) {
      return this.localPhaseMode() === 'both';
    }

    return false;
  }

  private isPhaseTwoRoute(route: string): boolean {
    const normalizedRoute = this.normalizeRoute(route);

    return UNRELEASED_ROUTE_PREFIXES.some(
      (prefix) => normalizedRoute === prefix || normalizedRoute.startsWith(`${prefix}/`),
    );
  }

  private isAdminToolsRoute(route: string): boolean {
    const normalizedRoute = this.normalizeRoute(route);

    return ADMIN_TOOL_ROUTE_PREFIXES.some(
      (prefix) => normalizedRoute === prefix || normalizedRoute.startsWith(`${prefix}/`),
    );
  }

  private normalizeRoute(route: string): string {
    const path = route.split('?')[0].split('#')[0].trim().toLowerCase();

    if (!path) return '/';

    const routeWithSlash = path.startsWith('/') ? path : `/${path}`;

    return routeWithSlash.length > 1 && routeWithSlash.endsWith('/')
      ? routeWithSlash.slice(0, -1)
      : routeWithSlash;
  }

  private isLocalHost(): boolean {
    return LOCAL_HOSTNAMES.has(this.browser.hostname);
  }

  private readLocalPhaseMode(): LocalLaunchPhaseMode {
    return this.browser.getLocalStorageItem(LOCAL_LAUNCH_PHASE_MODE_STORAGE_KEY) === 'both'
      ? 'both'
      : 'phase-one';
  }

  private writeLocalPhaseMode(value: LocalLaunchPhaseMode): void {
    if (value === 'both') {
      this.browser.setLocalStorageItem(LOCAL_LAUNCH_PHASE_MODE_STORAGE_KEY, 'both');
    } else {
      this.browser.removeLocalStorageItem(LOCAL_LAUNCH_PHASE_MODE_STORAGE_KEY);
    }
  }
}
